import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import { useHarshEventsBreakdownInformation } from "../hooks/useHarshDrivingReportsHooks";
import type { HarshDrivingReportsHookParams } from "../hooks/useHarshDrivingReportsHooks";
import { useProLogsTheme } from "../../../common/hooks/useProLogsTheme";
import ReactECharts from "echarts-for-react";
import "../fuel-economy-report/FuelEconomyReport.css";

const eventColors = {
  harshStop: "#9ca3af",
  harshBrake: "#a855f7",
  harshTurn: "#ec4899",
  harshAcceleration: "#f97316",
  speeding: "#38bdf8",
};

const formatTooltipPie = (params: unknown): string => {
  if (
    typeof params !== "object" ||
    params === null ||
    !("name" in params) ||
    !("value" in params) ||
    !("percent" in params)
  ) {
    return "";
  }
  const p = params as { name?: string; value?: number; percent?: number };
  return `${p.name ?? ""}: ${Number(p.value ?? 0)} (${Number(
    p.percent ?? 0
  ).toFixed(1)}%)`;
};

export interface HarshEventsActiveDriverWidgetProps
  extends HarshDrivingReportsHookParams {}

export const HarshEventsActiveDriverWidget: React.FC<HarshEventsActiveDriverWidgetProps> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const theme = useProLogsTheme();
  const {
    data,
    loading: isLoading,
    error,
  } = useHarshEventsBreakdownInformation({ fromDate, toDate, periodType });
  const isError = !!error;

  const activeDrivers = data?.activeDrivers ?? 0;
  const harshStop = data?.harshStop ?? 0;
  const harshBrake = data?.harshBraking ?? 0;
  const harshTurn = data?.harshTurn ?? 0;
  const harshAcceleration = data?.harshAcceleration ?? 0;
  const speeding = data?.speeding ?? 0;
  const totalAlerts =
    harshStop + harshBrake + harshTurn + harshAcceleration + speeding;

  const eventRows = useMemo(
    () => [
      {
        key: "harshStop",
        labelKey: "harshDrivingReport.harshStop",
        count: harshStop,
        color: eventColors.harshStop,
      },
      {
        key: "harshBrake",
        labelKey: "harshDrivingReport.harshBrake",
        count: harshBrake,
        color: eventColors.harshBrake,
      },
      {
        key: "harshTurn",
        labelKey: "harshDrivingReport.harshTurn",
        count: harshTurn,
        color: eventColors.harshTurn,
      },
      {
        key: "harshAcceleration",
        labelKey: "harshDrivingReport.harshAcceleration",
        count: harshAcceleration,
        color: eventColors.harshAcceleration,
      },
      {
        key: "speeding",
        labelKey: "harshDrivingReport.speeding",
        count: speeding,
        color: eventColors.speeding,
      },
    ],
    [harshStop, harshBrake, harshTurn, harshAcceleration, speeding]
  );

  const chartOption = useMemo(() => {
    const pieData = [
      {
        value: harshStop,
        name: translate("harshDrivingReport.harshStop", { _: "Harsh stop" }),
        itemStyle: { color: eventColors.harshStop },
      },
      {
        value: harshBrake,
        name: translate("harshDrivingReport.harshBrake", { _: "Harsh brake" }),
        itemStyle: { color: eventColors.harshBrake },
      },
      {
        value: harshTurn,
        name: translate("harshDrivingReport.harshTurn", { _: "Harsh turn" }),
        itemStyle: { color: eventColors.harshTurn },
      },
      {
        value: harshAcceleration,
        name: translate("harshDrivingReport.harshAcceleration", {
          _: "Harsh acceleration",
        }),
        itemStyle: { color: eventColors.harshAcceleration },
      },
      {
        value: speeding,
        name: translate("harshDrivingReport.speeding", { _: "Speeding" }),
        itemStyle: { color: eventColors.speeding },
      },
    ];

    return {
      tooltip: {
        trigger: "item",
        formatter: formatTooltipPie,
      },
      graphic: [
        {
          type: "text",
          left: "center",
          top: "45%",
          style: {
            text: String(totalAlerts),
            fontSize: 20,
            fontWeight: "bold",
            fill: theme.palette.text.primary,
          },
          z: 100,
        },
        {
          type: "text",
          left: "center",
          top: "60%",
          style: {
            text: translate("harshDrivingReport.alerts", { _: "Alerts" }),
            fontSize: 12,
            fill: theme.palette.text.secondary,
          },
          z: 100,
        },
      ],
      series: [
        {
          type: "pie",
          radius: ["60%", "75%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 0,
            borderColor: "#fff",
            borderWidth: 2,
          },
          label: {
            show: false,
          },
          emphasis: {
            label: {
              show: false,
            },
          },
          data: pieData,
        },
      ],
    };
  }, [
    harshStop,
    harshBrake,
    harshTurn,
    harshAcceleration,
    speeding,
    totalAlerts,
    translate,
    theme.palette.text.primary,
    theme.palette.text.secondary,
  ]);

  const maxCount = useMemo(
    () => Math.max(...eventRows.map((r) => r.count), 1),
    [eventRows]
  );

  return (
    <Card className="widget">
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("harshDrivingReport.harshEventsActiveDriver", {
            _: "Harsh Events Active Driver",
          })}
        </div>
      </div>
      <CardContent style={{ padding: 16 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            {/* Top: Active drivers + Donut */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {translate("harshDrivingReport.activeDrivers", {
                    _: "Active drivers",
                  })}
                </Typography>
                <Typography variant="h4" style={{ fontWeight: "bold" }}>
                  {activeDrivers}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {translate("harshDrivingReport.drivers", { _: "Drivers" })}
                </Typography>
              </Box>
              <Box width={160} height={160} flexShrink={0}>
                <ReactECharts
                  option={chartOption}
                  style={{ height: "100%", width: "100%" }}
                />
              </Box>
            </Box>

            <Box>
              {eventRows.map((row) => (
                <Box
                  key={row.key}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  style={{
                    padding: "6px 8px",
                    marginBottom: 4,
                    backgroundColor:
                      theme.palette.type === "dark"
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.04)",
                    borderRadius: 4,
                  }}
                >
                  <Typography
                    variant="body2"
                    style={{ flexShrink: 0, marginRight: 8 }}
                  >
                    {translate(row.labelKey, { _: row.labelKey })}
                  </Typography>
                  <Box flex={1} mx={1} minWidth={0}>
                    <Box
                      style={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor:
                          theme.palette.type === "dark"
                            ? "rgba(255,255,255,0.12)"
                            : "rgba(0,0,0,0.08)",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        style={{
                          height: "100%",
                          width: `${
                            maxCount > 0 ? (row.count / maxCount) * 100 : 0
                          }%`,
                          backgroundColor: row.color,
                          borderRadius: 3,
                          transition: "width 0.2s ease",
                        }}
                      />
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    style={{ flexShrink: 0, minWidth: 24, textAlign: "right" }}
                  >
                    {row.count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default HarshEventsActiveDriverWidget;
